/* eslint-disable react/require-default-props */
/* eslint-disable react/no-unused-prop-types */
/* eslint-disable import/no-unresolved */
/* eslint-disable security/detect-object-injection */
import { memo, useEffect, useMemo } from "react";
import { FormikProps, getIn } from "formik";
import Button from "@mui/material/Button";
import Resources from "src/config/Resources";
import { useSelector } from "react-redux";
import { useAppSelector } from "src/utils/hooks/useAppSelector";
import CustomCheckBox from "src/shared/CustomCheckBox";
import { omit } from "lodash";
import dayjs from "dayjs";
import { ifscExistCall, pincodeCall } from "src/services/Master";
import { SET_TOAST_ERROR } from "src/redux/Actions/common";
import store from "src/redux/store";
import {
  addOrdinalSuffix,
  calcAgeFromToday,
  concatDetails,
  findKeysByValue,
  findValueByLabel
} from "../../../../../helpers/FormDetails/ProposalDetails/resources";
import { NomineeDetails } from "../../../../../helpers/FormDetails/ProposalDetails/interfaces";
import CustomDatePicker from "../../../../../shared/CustomDatePicker";
import CustomRadioButton from "../../../../../shared/CustomRadioButton";
import CustomTextField from "../../../../../shared/CustomTextField";
import CustomSelect from "../../../../../shared/CustomSelect";

interface RelationshipItem {
  label: string;
  value: string;
}

type RelationshipDetails = Record<string, RelationshipItem[]>;

interface Props {
  formik: FormikProps<NomineeDetails>;
  nomineeNumber: number;
  nomineeDetailsSchema: any;
  onRemoveClick: any;
  savePincodeDataToStore?: (data: any) => void;
  customError: any;
  setCustomError: (val: any) => void;
}

const combinedSelector = (state: {
  masterdata_reducer: { genderDetails: any; relationshipDetails: any };
}) => {
  const genderData = state.masterdata_reducer.genderDetails;
  const relationData = state.masterdata_reducer.relationshipDetails;

  const relationDataList: string[] = relationData ? Object.values(relationData) : [];
  const genderDataList: string[] = genderData ? Object.values(genderData) : [];

  return {
    genderDataList,
    relationDataList
  };
};

function PrimaryDetails({
  formik: { errors, handleBlur, setFieldTouched, touched, values, setFieldValue, handleChange },
  customError,
  setCustomError,
  nomineeNumber,
  onRemoveClick,
  nomineeDetailsSchema
}: Props) {
  const masterData = useAppSelector((state) => state.masterdata_reducer);
  const { genderDataList } = useSelector(combinedSelector);
  const proposerAddressDetails = useAppSelector((state) => state.proposerDetails.address?.address);
  const nomineeDetails = useAppSelector((state) => state.proposerDetails.nominee?.nominee);

  const getFieldConfig = (fieldName: string, isAppointee = false) => {
    if (isAppointee) {
      return nomineeDetailsSchema.appointee[fieldName];
    }
    return nomineeDetailsSchema[fieldName];
  };

  const isFieldVisible = (fieldName: string, isAppointee = false) => {
    const config = getFieldConfig(fieldName, isAppointee);
    return config?.visible !== false;
  };

  const isFieldRequired = (fieldName: string, isAppointee = false) => {
    const config = getFieldConfig(fieldName, isAppointee);
    return config?.required;
  };

  const getFieldLabel = (fieldName: string, isAppointee = false) => {
    const config = getFieldConfig(fieldName, isAppointee);
    return config?.label || fieldName;
  };

  const getFieldDefaultValue = (fieldName: string, isAppointee = false) => {
    const config = getFieldConfig(fieldName, isAppointee);
    return config?.defaultValue;
  };

  const currentNominee = values.nominee[nomineeNumber];

  const isAnyNomineeBankFieldFilled =
    currentNominee?.captureBankDetails &&
    (currentNominee?.bankName || currentNominee?.bankAccountNumber || currentNominee?.ifscCode);

  const isAnyAppointeeBankFieldFilled =
    currentNominee?.captureAppointeeBankDetails &&
    (currentNominee?.appointeeBankName ||
      currentNominee?.appointeeBankAccountNumber ||
      currentNominee?.appointeeIfscCode);

  const isAnyAppointeeAddressFieldFilled =
    currentNominee?.captureAppointeeAddress &&
    (currentNominee?.appointeeAddress || currentNominee?.appointeePincode);

  useEffect(() => {
    setFieldValue(
      `nominee[${nomineeNumber}].genderCode`,
      findKeysByValue(masterData?.genderDetails, values?.nominee[nomineeNumber]?.gender)
    );
  }, [values.nominee[nomineeNumber]?.gender]);

  useEffect(() => {
    setFieldValue(
      `nominee[${nomineeNumber}].titleCode`,
      findValueByLabel(masterData?.salutationDetails, values?.nominee[nomineeNumber]?.title)
    );
  }, [values.nominee[nomineeNumber]?.title]);

  const handleGenderClick = () => {
    const genderCode = values?.nominee?.[nomineeNumber]?.genderCode;

    if (genderCode && masterData?.salutationDetails?.[genderCode]?.length) {
      setFieldValue(
        `nominee[${nomineeNumber}].title`,
        masterData?.salutationDetails[genderCode][0]?.label
      );
      setFieldValue(`nominee[${nomineeNumber}].relationship`, "");
    }
  };

  useEffect(() => {
    handleGenderClick();
  }, [values.nominee[nomineeNumber]?.genderCode]);

  useEffect(() => {
    setFieldValue(
      `nominee[${nomineeNumber}].appointeeGenderCode`,
      findKeysByValue(masterData?.genderDetails, values.nominee[nomineeNumber]?.appointeeGender)
    );
  }, [values.nominee[nomineeNumber]?.appointeeGender]);

  useEffect(() => {
    setFieldValue(
      `nominee[${nomineeNumber}].appointeeTitleCode`,
      findValueByLabel(
        masterData?.salutationDetails,
        values?.nominee[nomineeNumber]?.appointeeTitle
      )
    );
  }, [values.nominee[nomineeNumber]?.appointeeTitle]);

  const handleAppointeeGenderClick = () => {
    const genderCode = values?.nominee?.[nomineeNumber]?.appointeeGenderCode;

    if (genderCode && masterData?.salutationDetails?.[genderCode]?.length) {
      setFieldValue(
        `nominee[${nomineeNumber}].appointeeTitle`,
        masterData?.salutationDetails[genderCode][0]?.label
      );
      setFieldValue(`nominee[${nomineeNumber}].appointeeRelationship`, "");
    }
  };

  useEffect(() => {
    handleAppointeeGenderClick();
  }, [values.nominee[nomineeNumber]?.appointeeGenderCode]);

  useEffect(() => {
    setFieldValue(
      `nominee[${nomineeNumber}].relationshipCode`,
      findValueByLabel(
        masterData?.relationshipDetails,
        values?.nominee[nomineeNumber]?.relationship
      )
    );
  }, [values?.nominee[nomineeNumber]?.relationship]);

  useEffect(() => {
    setFieldValue(
      `nominee[${nomineeNumber}].appointeeRelationCode`,
      findValueByLabel(
        masterData?.relationshipDetails,
        values?.nominee[nomineeNumber]?.appointeeRelation
      )
    );
  }, [values?.nominee[nomineeNumber]?.appointeeRelation]);

  useEffect(() => {
    if (calcAgeFromToday(values?.nominee[nomineeNumber]?.dob) > 18) {
      setFieldValue(`nominee[${nomineeNumber}].appointeeRelation`, "");
      setFieldValue(`nominee[${nomineeNumber}].appointeeRelationCode`, "");
      setFieldValue(`nominee[${nomineeNumber}].appointeeName`, "");
      setFieldValue(`nominee[${nomineeNumber}].appointeeDob`, "");
      setFieldValue(`nominee[${nomineeNumber}].appointeeEmail`, "");
      setFieldValue(`nominee[${nomineeNumber}].appointeeMobileNo`, "");
      setFieldValue(`nominee[${nomineeNumber}].appointeeAddress`, "");
      setFieldValue(`nominee[${nomineeNumber}].appointeePincode`, "");
      setFieldValue(`nominee[${nomineeNumber}].captureAppointeeBankDetails`, false);
      setFieldValue(`nominee[${nomineeNumber}].appointeeBankName`, "");
      setFieldValue(`nominee[${nomineeNumber}].appointeeBankAccountNumber`, "");
      setFieldValue(`nominee[${nomineeNumber}].appointeeIfscCode`, "");
    }
  }, [values.nominee[nomineeNumber]?.dob]);

  const handleAddressValue = async () => {
    const isSame = values?.nominee[nomineeNumber]?.isAddressSame;
    if (isSame) {
      const addressValue = await concatDetails(
        omit(proposerAddressDetails, ["cityCode", "stateCode"])
      );
      const pincode = proposerAddressDetails?.pinCode || "";
      setFieldValue(`nominee[${nomineeNumber}].address`, addressValue);
      setFieldValue(`nominee[${nomineeNumber}].pincode`, pincode);
      setFieldTouched(`nominee[${nomineeNumber}].address`, false);
      setFieldTouched(`nominee[${nomineeNumber}].pincode`, false);
      setCustomError((prev: any) => ({
        ...prev,
        [`nominee[${nomineeNumber}].pincode`]: null
      }));
    } else {
      setFieldValue(
        `nominee[${nomineeNumber}].address`,
        nomineeDetails?.[nomineeNumber]?.address || ""
      );
      setFieldValue(
        `nominee[${nomineeNumber}].pincode`,
        nomineeDetails?.[nomineeNumber]?.pincode || ""
      );
    }
  };

  useEffect(() => {
    handleAddressValue();
  }, [values?.nominee[nomineeNumber]?.isAddressSame]);

  const handleAppointeeAddressValue = async () => {
    const isSame = values?.nominee[nomineeNumber]?.isAppointeeAddressSameAsProposer;
    if (isSame) {
      const proposerAddress = await concatDetails(
        omit(proposerAddressDetails, ["cityCode", "stateCode"])
      );
      const proposerPincode = proposerAddressDetails?.pinCode || "";

      setFieldValue(`nominee[${nomineeNumber}].appointeeAddress`, proposerAddress);
      setFieldValue(`nominee[${nomineeNumber}].appointeePincode`, proposerPincode);
      setFieldTouched(`nominee[${nomineeNumber}].appointeeAddress`, false);
      setFieldTouched(`nominee[${nomineeNumber}].appointeePincode`, false);
      setCustomError((prev: any) => ({
        ...prev,
        [`nominee[${nomineeNumber}].appointeePincode`]: null
      }));
    } else {
      setFieldValue(
        `nominee[${nomineeNumber}].appointeeAddress`,
        nomineeDetails?.[nomineeNumber]?.appointeeAddress || ""
      );
      setFieldValue(
        `nominee[${nomineeNumber}].appointeePincode`,
        nomineeDetails?.[nomineeNumber]?.appointeePincode || ""
      );
    }
  };

  useEffect(() => {
    handleAppointeeAddressValue();
  }, [values?.nominee[nomineeNumber]?.isAppointeeAddressSameAsProposer]);

  useEffect(() => {
    if (!values?.nominee[nomineeNumber]?.captureBankDetails) {
      setFieldValue(`nominee[${nomineeNumber}].bankName`, "");
      setFieldValue(`nominee[${nomineeNumber}].bankAccountNumber`, "");
      setFieldValue(`nominee[${nomineeNumber}].ifscCode`, "");
      setFieldTouched(`nominee[${nomineeNumber}].bankName`, false);
      setFieldTouched(`nominee[${nomineeNumber}].bankAccountNumber`, false);
      setFieldTouched(`nominee[${nomineeNumber}].ifscCode`, false);
    }
  }, [values?.nominee[nomineeNumber]?.captureBankDetails]);
  useEffect(() => {
    if (!values?.nominee[nomineeNumber]?.captureAppointeeAddress) {
      setFieldValue(`nominee[${nomineeNumber}].appointeeBankName`, "");
      setFieldValue(`nominee[${nomineeNumber}].appointeeBankAccountNumber`, "");
      setFieldValue(`nominee[${nomineeNumber}].appointeeIfscCode`, "");
      setFieldTouched(`nominee[${nomineeNumber}].appointeeBankName`, false);
      setFieldTouched(`nominee[${nomineeNumber}].appointeeBankAccountNumber`, false);
      setFieldTouched(`nominee[${nomineeNumber}].appointeeIfscCode`, false);
    }
    if (!values?.nominee[nomineeNumber]?.captureAppointeeBankDetails) {
      // setFieldValue(`nominee[${nomineeNumber}].appointeePincode`, "");
      // setFieldValue(`nominee[${nomineeNumber}].appointeeAddress`, "");
      setFieldTouched(`nominee[${nomineeNumber}].appointeePincode`, false);
      setFieldTouched(`nominee[${nomineeNumber}].appointeeAddress`, false);
    }
  }, [
    values?.nominee[nomineeNumber]?.captureAppointeeAddress,
    values?.nominee[nomineeNumber]?.captureAppointeeBankDetails
  ]);

  const handlePinCodeValidation = async (event: any, field: string) => {
    const pincodeValue: any = event?.target?.value || "";
    if (pincodeValue !== getIn(values, field)) {
      await setFieldValue(field, pincodeValue);
      setFieldTouched(field, true);
      if (pincodeValue && pincodeValue.length === 6) {
        try {
          const response = await pincodeCall(pincodeValue);
          if (!response.data) {
            store.dispatch(
              SET_TOAST_ERROR({
                error: true,
                duration: 6000,
                message: "Pincode mismatch. Retry with another value"
              })
            );
            setCustomError((prev: any) => ({ ...prev, [field]: "Invalid Pincode" }));
            return false;
          }
          setCustomError((prev: any) => ({ ...prev, [field]: null }));
          return true;
        } catch (error) {
          return false;
        }
      }
    }
    return true;
  };

  const handleIfscCodeValidation = async (event: any, field: string) => {
    const ifscCodeValue: any = event?.target?.value || "";
    if (ifscCodeValue !== getIn(values, field)) {
      await setFieldValue(field, ifscCodeValue);
      setFieldTouched(field, true);
      if (ifscCodeValue && ifscCodeValue.length === 11) {
        try {
          const response = await ifscExistCall(ifscCodeValue.toUpperCase());
          if (!response.data.exist) {
            store.dispatch(
              SET_TOAST_ERROR({
                error: true,
                duration: 6000,
                message: "IFSC code mismatch. Retry with another value"
              })
            );
            setCustomError((prev: any) => ({ ...prev, [field]: "Invalid IFSC code" }));
            return false;
          }
          setCustomError((prev: any) => ({ ...prev, [field]: null }));
          return true;
        } catch (error) {
          return false;
        }
      }
    }
    return true;
  };

  const nomineeGenderCode = values?.nominee?.[nomineeNumber]?.genderCode;
  const appointeeGenderCode = values?.nominee?.[nomineeNumber]?.appointeeGenderCode;

  const salutationOptions =
    nomineeGenderCode && masterData?.salutationDetails?.[nomineeGenderCode]
      ? masterData?.salutationDetails[nomineeGenderCode].map(
          (item: { label: string; value: string }) => item.label
        )
      : [];

  const salutationAppointeeOptions =
    appointeeGenderCode && masterData?.salutationDetails?.[appointeeGenderCode]
      ? masterData?.salutationDetails[appointeeGenderCode].map(
          (item: { label: string; value: string }) => item.label
        )
      : [];

  const relationshipOptions = useMemo(() => {
    const genderCode = values?.nominee?.[nomineeNumber]?.genderCode;
    if (genderCode && masterData?.relationshipDetails?.[genderCode]) {
      return masterData.relationshipDetails[genderCode].map((item: any) => item.label);
    }
    if (masterData?.relationshipDetails) {
      const relationshipDetails = masterData.relationshipDetails as RelationshipDetails;
      const allRelationships = Object.values(relationshipDetails).flatMap((genderRelationships) =>
        genderRelationships.map((item) => item.label)
      );
      return Array.from(new Set(allRelationships));
    }
    return [];
  }, [values?.nominee?.[nomineeNumber]?.genderCode, masterData?.relationshipDetails]);

  const relationshipAppointeeOptions =
    appointeeGenderCode && masterData?.relationshipDetails[appointeeGenderCode]
      ? masterData.relationshipDetails[appointeeGenderCode].map(
          (item: { label: string; value: string }) => item.label
        )
      : [];

  return (
    <div>
      <div className="items-center justify-between mb-6">
        <div>
          <div className="flex justify-between">
            <div className="font-montserrat text-black text-base font-bold !my-auto">
              {`${nomineeNumber > 0 ? addOrdinalSuffix(nomineeNumber + 1) : ""} Nominee Details`}
            </div>
            {nomineeNumber > 0 && (
              <div>
                <Button
                  variant="outlined"
                  onClick={onRemoveClick}
                  classes={{ root: "!px-0" }}
                  className="!text-black !uppercase h-10 !rounded-lg !border-2"
                >
                  <img src={Resources.images.FormDetails.removeIcon} alt="remove" />
                </Button>
              </div>
            )}
          </div>
          <div>
            {nomineeNumber === 0 && (
              <div className="font-montserrat text-black text-sm !font-normal ">
                Particulars of the nominee (As per section 39 of Insurance Act 1938) (The person to
                whom the money secured by the policy shall be paid)
              </div>
            )}
          </div>
        </div>
      </div>
      <div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-10 gap-y-6">
          {isFieldVisible("title") && (
            <CustomSelect
              requiredStar={isFieldRequired("title")}
              label={getFieldLabel("title")}
              options={salutationOptions}
              onChange={handleChange}
              setFieldTouched={setFieldTouched}
              name={`nominee[${nomineeNumber}].title`}
              value={
                salutationOptions.includes(values?.nominee[nomineeNumber]?.title)
                  ? values.nominee[nomineeNumber]?.title
                  : ""
              }
              error={getIn(errors, `nominee[${nomineeNumber}].title`)}
              touched={getIn(touched, `nominee[${nomineeNumber}].title`)}
            />
          )}

          {isFieldVisible("gender") && (
            <CustomRadioButton
              requiredStar={isFieldRequired("gender")}
              label={getFieldLabel("gender")}
              className="col-span-1 sm:col-span-3"
              options={genderDataList}
              setFieldValue={setFieldValue}
              setFieldTouched={setFieldTouched}
              defaultValue={
                values?.nominee[nomineeNumber]?.gender || getFieldDefaultValue("gender")
              }
              name={`nominee[${nomineeNumber}].gender`}
              error={getIn(errors, `nominee[${nomineeNumber}].gender`)}
              touched={getIn(touched, `nominee[${nomineeNumber}].gender`)}
            />
          )}
          {isFieldVisible("fullName") && (
            <CustomTextField
              requiredStar={isFieldRequired("fullName")}
              label={getFieldLabel("fullName")}
              onChange={handleChange}
              handleBlur={handleBlur}
              value={values?.nominee[nomineeNumber]?.fullName ?? getFieldDefaultValue("fullName")}
              name={`nominee[${nomineeNumber}].fullName`}
              error={getIn(errors, `nominee[${nomineeNumber}].fullName`)}
              touched={getIn(touched, `nominee[${nomineeNumber}].fullName`)}
            />
          )}

          {isFieldVisible("dob") && (
            <CustomDatePicker
              requiredStar={isFieldRequired("dob")}
              disableFuture
              minDate={dayjs("1900-01-01")}
              label={getFieldLabel("dob")}
              setFieldValue={setFieldValue}
              setFieldTouched={setFieldTouched}
              name={`nominee[${nomineeNumber}].dob`}
              value={values.nominee[nomineeNumber]?.dob ?? getFieldDefaultValue("dob")}
              error={getIn(errors, `nominee[${nomineeNumber}].dob`)}
              touched={getIn(touched, `nominee[${nomineeNumber}].dob`)}
            />
          )}

          {isFieldVisible("relationshipWithPrimary") && (
            <CustomSelect
              requiredStar={isFieldRequired("relationshipWithPrimary")}
              label={getFieldLabel("relationshipWithPrimary")}
              options={relationshipOptions}
              onChange={handleChange}
              setFieldTouched={setFieldTouched}
              name={`nominee[${nomineeNumber}].relationship`}
              value={
                relationshipOptions.includes(values?.nominee[nomineeNumber]?.relationship)
                  ? values?.nominee[nomineeNumber]?.relationship
                  : ""
              }
              error={getIn(errors, `nominee[${nomineeNumber}].relationship`)}
              touched={getIn(touched, `nominee[${nomineeNumber}].relationship`)}
            />
          )}

          {isFieldVisible("sharePercentage") && (
            <CustomTextField
              requiredStar={isFieldRequired("sharePercentage")}
              label={getFieldLabel("sharePercentage")}
              onChange={handleChange}
              handleBlur={handleBlur}
              value={
                values?.nominee[nomineeNumber]?.share ?? getFieldDefaultValue("sharePercentage")
              }
              name={`nominee[${nomineeNumber}].share`}
              error={
                getIn(errors, `nominee[${nomineeNumber}].share`) ||
                (typeof errors?.nominee === "string" ? errors.nominee : undefined)
              }
              touched={getIn(touched, `nominee[${nomineeNumber}].share`)}
            />
          )}

          {isFieldVisible("email") && (
            <CustomTextField
              requiredStar={isFieldRequired("email")}
              label={getFieldLabel("email")}
              onChange={handleChange}
              handleBlur={handleBlur}
              value={values?.nominee[nomineeNumber]?.email ?? getFieldDefaultValue("email")}
              name={`nominee[${nomineeNumber}].email`}
              error={getIn(errors, `nominee[${nomineeNumber}].email`)}
              touched={getIn(touched, `nominee[${nomineeNumber}].email`)}
            />
          )}

          {isFieldVisible("mobileNo") && (
            <CustomTextField
              requiredStar={isFieldRequired("mobileNo")}
              label={getFieldLabel("mobileNo")}
              onChange={handleChange}
              handleBlur={handleBlur}
              value={values?.nominee[nomineeNumber]?.mobileNo ?? getFieldDefaultValue("mobileNo")}
              name={`nominee[${nomineeNumber}].mobileNo`}
              error={getIn(errors, `nominee[${nomineeNumber}].mobileNo`)}
              touched={getIn(touched, `nominee[${nomineeNumber}].mobileNo`)}
            />
          )}
          {isFieldVisible("address") && (
            <CustomTextField
              requiredStar={isFieldRequired("address")}
              disabledField={values?.nominee[nomineeNumber]?.isAddressSame}
              label={getFieldLabel("address")}
              onChange={handleChange}
              handleBlur={handleBlur}
              value={values?.nominee[nomineeNumber]?.address ?? getFieldDefaultValue("address")}
              name={`nominee[${nomineeNumber}].address`}
              error={getIn(errors, `nominee[${nomineeNumber}].address`)}
              touched={getIn(touched, `nominee[${nomineeNumber}].address`)}
            />
          )}
          {isFieldVisible("pincode") && (
            <CustomTextField
              requiredStar={isFieldRequired("pincode")}
              disabledField={values?.nominee[nomineeNumber]?.isAddressSame}
              maxLength={6}
              label={getFieldLabel("pincode")}
              onChange={(e) => handlePinCodeValidation(e, `nominee[${nomineeNumber}].pincode`)}
              handleBlur={handleBlur}
              value={values?.nominee[nomineeNumber]?.pincode ?? getFieldDefaultValue("pincode")}
              name={`nominee[${nomineeNumber}].pincode`}
              error={
                customError[`nominee[${nomineeNumber}].pincode`] ||
                getIn(errors, `nominee[${nomineeNumber}].pincode`)
              }
              touched={getIn(touched, `nominee[${nomineeNumber}].pincode`)}
            />
          )}
        </div>

        {/* {isFieldVisible("captureAddress") && (
          <div className="mt-5 ">
            <CustomCheckBox
              label={getFieldLabel("captureAddress")}
              name={`nominee[${nomineeNumber}].captureAddress`}
              handleBlur={handleBlur}
              setFieldValue={setFieldValue}
              checked={
                values?.nominee[nomineeNumber]?.captureAddress ??
                getFieldDefaultValue("captureAddress")
              }
            />
          </div>
        )} */}
        <div className="display-flex grid grid-cols-1 mt-5 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-10 gap-y-6">
          {isFieldVisible("isAddressSameAsLA") && (
            <div className="w-50 mt-5">
              <CustomCheckBox
                label={getFieldLabel("isAddressSameAsLA")}
                name={`nominee[${nomineeNumber}].isAddressSame`}
                handleBlur={handleBlur}
                setFieldValue={setFieldValue}
                checked={
                  values?.nominee[nomineeNumber]?.isAddressSame ??
                  getFieldDefaultValue("isAddressSameAsLA")
                }
              />
            </div>
          )}
          {isFieldVisible("captureBankDetails") && (
            <div className="mt-5">
              <CustomCheckBox
                label={getFieldLabel("captureBankDetails")}
                name={`nominee[${nomineeNumber}].captureBankDetails`}
                handleBlur={handleBlur}
                setFieldValue={setFieldValue}
                checked={
                  values?.nominee[nomineeNumber]?.captureBankDetails ??
                  getFieldDefaultValue("captureBankDetails")
                }
              />
            </div>
          )}
        </div>
        {isFieldVisible("bankDetails") && values?.nominee[nomineeNumber]?.captureBankDetails && (
          <div className="grid grid-cols-1 mt-5 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-10 gap-y-6">
            <CustomTextField
              requiredStar={isFieldRequired("bankName") || isAnyNomineeBankFieldFilled}
              label={getFieldLabel("bankName")}
              onChange={handleChange}
              handleBlur={handleBlur}
              value={values?.nominee[nomineeNumber]?.bankName ?? getFieldDefaultValue("bankName")}
              name={`nominee[${nomineeNumber}].bankName`}
              error={getIn(errors, `nominee[${nomineeNumber}].bankName`)}
              touched={getIn(touched, `nominee[${nomineeNumber}].bankName`)}
            />
            <CustomTextField
              requiredStar={isFieldRequired("bankAccountNumber") || isAnyNomineeBankFieldFilled}
              label={getFieldLabel("bankAccountNumber")}
              onChange={handleChange}
              handleBlur={handleBlur}
              value={
                values?.nominee[nomineeNumber]?.bankAccountNumber ??
                getFieldDefaultValue("bankAccountNumber")
              }
              name={`nominee[${nomineeNumber}].bankAccountNumber`}
              error={getIn(errors, `nominee[${nomineeNumber}].bankAccountNumber`)}
              touched={getIn(touched, `nominee[${nomineeNumber}].bankAccountNumber`)}
            />
            <CustomTextField
              requiredStar={isFieldRequired("ifscCode") || isAnyNomineeBankFieldFilled}
              label={getFieldLabel("ifscCode")}
              onChange={(e) => handleIfscCodeValidation(e, `nominee[${nomineeNumber}].ifscCode`)}
              handleBlur={handleBlur}
              value={values?.nominee[nomineeNumber]?.ifscCode ?? getFieldDefaultValue("ifscCode")}
              name={`nominee[${nomineeNumber}].ifscCode`}
              error={
                customError[`nominee[${nomineeNumber}].ifscCode`] ||
                getIn(errors, `nominee[${nomineeNumber}].ifscCode`)
              }
              touched={getIn(touched, `nominee[${nomineeNumber}].ifscCode`)}
            />
          </div>
        )}

        {/* {isFieldVisible("captureAddress") && (
          <div className="mt-5 ">
            <CustomCheckBox
              label={getFieldLabel("captureAddress")}
              name={`nominee[${nomineeNumber}].captureAddress`}
              handleBlur={handleBlur}
              setFieldValue={setFieldValue}
              checked={
                values?.nominee[nomineeNumber]?.captureAddress ??
                getFieldDefaultValue("captureAddress")
              }
            />
          </div>
        )} */}

        {/* {values?.nominee[nomineeNumber]?.captureAddress && (
          <>
            <div className="grid grid-cols-1 mt-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-10 gap-y-6">
              {isFieldVisible("address") && (
                <CustomTextField
                  requiredStar={isFieldRequired("address") || isAnyNomineeAddressFieldFilled}
                  disabledField={values?.nominee[nomineeNumber]?.isAddressSame}
                  label={getFieldLabel("address")}
                  onChange={handleChange}
                  handleBlur={handleBlur}
                  value={values?.nominee[nomineeNumber]?.address ?? getFieldDefaultValue("address")}
                  name={`nominee[${nomineeNumber}].address`}
                  error={getIn(errors, `nominee[${nomineeNumber}].address`)}
                  touched={getIn(touched, `nominee[${nomineeNumber}].address`)}
                />
              )}
              {isFieldVisible("pincode") && (
                <CustomTextField
                  requiredStar={isFieldRequired("pincode") || isAnyNomineeAddressFieldFilled}
                  disabledField={values?.nominee[nomineeNumber]?.isAddressSame}
                  maxLength={6}
                  label={getFieldLabel("pincode")}
                  onChange={(e) => handlePinCodeValidation(e, `nominee[${nomineeNumber}].pincode`)}
                  handleBlur={handleBlur}
                  value={values?.nominee[nomineeNumber]?.pincode ?? getFieldDefaultValue("pincode")}
                  name={`nominee[${nomineeNumber}].pincode`}
                  error={
                    customError[`nominee[${nomineeNumber}].pincode`] ||
                    getIn(errors, `nominee[${nomineeNumber}].pincode`)
                  }
                  touched={getIn(touched, `nominee[${nomineeNumber}].pincode`)}
                />
              )}
            </div>

            {isFieldVisible("isAddressSameAsLA") && (
              <div className="mt-5">
                <CustomCheckBox
                  label={getFieldLabel("isAddressSameAsLA")}
                  name={`nominee[${nomineeNumber}].isAddressSame`}
                  handleBlur={handleBlur}
                  setFieldValue={setFieldValue}
                  checked={
                    values?.nominee[nomineeNumber]?.isAddressSame ??
                    getFieldDefaultValue("isAddressSameAsLA")
                  }
                />
              </div>
            )}
          </>
        )} */}
        {calcAgeFromToday(values.nominee[nomineeNumber]?.dob) < 18 && (
          <div className="mt-10">
            <span className="font-montserrat text-black text-base font-bold mb-6 !my-auto">
              Nominee is minor, please fill the respective details
            </span>
            <div className="grid grid-cols-1 mt-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-10 gap-y-6">
              {isFieldVisible("title", true) && (
                <CustomSelect
                  requiredStar={isFieldRequired("title", true)}
                  label={getFieldLabel("title", true)}
                  options={salutationAppointeeOptions}
                  onChange={handleChange}
                  setFieldTouched={setFieldTouched}
                  name={`nominee[${nomineeNumber}].appointeeTitle`}
                  value={
                    salutationAppointeeOptions.includes(
                      values?.nominee[nomineeNumber]?.appointeeTitle
                    )
                      ? values?.nominee[nomineeNumber]?.appointeeTitle
                      : ""
                  }
                  error={getIn(errors, `nominee[${nomineeNumber}].appointeeTitle`)}
                  touched={getIn(touched, `nominee[${nomineeNumber}].appointeeTitle`)}
                />
              )}
              {isFieldVisible("gender", true) && (
                <CustomRadioButton
                  requiredStar={isFieldRequired("gender", true)}
                  label={getFieldLabel("gender", true)}
                  className="col-span-1 sm:col-span-3"
                  options={genderDataList}
                  setFieldValue={setFieldValue}
                  setFieldTouched={setFieldTouched}
                  defaultValue={
                    values?.nominee[nomineeNumber]?.appointeeGender ||
                    getFieldDefaultValue("gender", true)
                  }
                  name={`nominee[${nomineeNumber}].appointeeGender`}
                  error={getIn(errors, `nominee[${nomineeNumber}].appointeeGender`)}
                  touched={getIn(touched, `nominee[${nomineeNumber}].appointeeGender`)}
                />
              )}
              {isFieldVisible("fullName", true) && (
                <CustomTextField
                  requiredStar={isFieldRequired("fullName", true)}
                  label={getFieldLabel("fullName", true)}
                  onChange={handleChange}
                  handleBlur={handleBlur}
                  value={
                    values?.nominee[nomineeNumber]?.appointeeName ??
                    getFieldDefaultValue("fullName", true)
                  }
                  name={`nominee[${nomineeNumber}].appointeeName`}
                  error={getIn(errors, `nominee[${nomineeNumber}].appointeeName`)}
                  touched={getIn(touched, `nominee[${nomineeNumber}].appointeeName`)}
                />
              )}

              {isFieldVisible("dob", true) && (
                <CustomDatePicker
                  requiredStar={isFieldRequired("dob", true)}
                  label={getFieldLabel("dob", true)}
                  setFieldValue={setFieldValue}
                  setFieldTouched={setFieldTouched}
                  name={`nominee[${nomineeNumber}].appointeeDob`}
                  value={
                    values.nominee[nomineeNumber]?.appointeeDob ?? getFieldDefaultValue("dob", true)
                  }
                  error={getIn(errors, `nominee[${nomineeNumber}].appointeeDob`)}
                  touched={getIn(touched, `nominee[${nomineeNumber}].appointeeDob`)}
                />
              )}

              {isFieldVisible("relationshipWithPrimary", true) && (
                <CustomSelect
                  requiredStar={isFieldRequired("relationshipWithPrimary", true)}
                  label={getFieldLabel("relationshipWithPrimary", true)}
                  options={relationshipAppointeeOptions}
                  onChange={handleChange}
                  setFieldTouched={setFieldTouched}
                  name={`nominee[${nomineeNumber}].appointeeRelation`}
                  value={
                    relationshipAppointeeOptions.includes(
                      values?.nominee[nomineeNumber].appointeeRelation
                    )
                      ? values?.nominee[nomineeNumber].appointeeRelation
                      : ""
                  }
                  error={getIn(errors, `nominee[${nomineeNumber}].appointeeRelation`)}
                  touched={getIn(touched, `nominee[${nomineeNumber}].appointeeRelation`)}
                />
              )}

              {isFieldVisible("email", true) && (
                <CustomTextField
                  requiredStar={isFieldRequired("email", true)}
                  label={getFieldLabel("email", true)}
                  onChange={handleChange}
                  handleBlur={handleBlur}
                  value={
                    values?.nominee[nomineeNumber]?.appointeeEmail ??
                    getFieldDefaultValue("email", true)
                  }
                  name={`nominee[${nomineeNumber}].appointeeEmail`}
                  error={getIn(errors, `nominee[${nomineeNumber}].appointeeEmail`)}
                  touched={getIn(touched, `nominee[${nomineeNumber}].appointeeEmail`)}
                />
              )}

              {isFieldVisible("mobileNo", true) && (
                <CustomTextField
                  requiredStar={isFieldRequired("mobileNo", true)}
                  label={getFieldLabel("mobileNo", true)}
                  onChange={handleChange}
                  handleBlur={handleBlur}
                  value={
                    values?.nominee[nomineeNumber]?.appointeeMobileNo ??
                    getFieldDefaultValue("mobileNo", true)
                  }
                  name={`nominee[${nomineeNumber}].appointeeMobileNo`}
                  error={getIn(errors, `nominee[${nomineeNumber}].appointeeMobileNo`)}
                  touched={getIn(touched, `nominee[${nomineeNumber}].appointeeMobileNo`)}
                />
              )}
              {isFieldVisible("address", true) && (
                <CustomTextField
                  requiredStar={
                    isFieldRequired("address", true) || isAnyAppointeeAddressFieldFilled
                  }
                  disabledField={values?.nominee[nomineeNumber]?.isAppointeeAddressSameAsProposer}
                  label={getFieldLabel("address", true)}
                  onChange={handleChange}
                  handleBlur={handleBlur}
                  value={
                    values?.nominee[nomineeNumber]?.appointeeAddress ??
                    getFieldDefaultValue("address", true)
                  }
                  name={`nominee[${nomineeNumber}].appointeeAddress`}
                  error={getIn(errors, `nominee[${nomineeNumber}].appointeeAddress`)}
                  touched={getIn(touched, `nominee[${nomineeNumber}].appointeeAddress`)}
                />
              )}
              {isFieldVisible("pincode", true) && (
                <CustomTextField
                  requiredStar={
                    isFieldRequired("pincode", true) || isAnyAppointeeAddressFieldFilled
                  }
                  disabledField={values?.nominee[nomineeNumber]?.isAppointeeAddressSameAsProposer}
                  maxLength={6}
                  label={getFieldLabel("pincode", true)}
                  onChange={(e) =>
                    handlePinCodeValidation(e, `nominee[${nomineeNumber}].appointeePincode`)
                  }
                  handleBlur={handleBlur}
                  value={
                    values?.nominee[nomineeNumber]?.appointeePincode ??
                    getFieldDefaultValue("pincode", true)
                  }
                  name={`nominee[${nomineeNumber}].appointeePincode`}
                  error={
                    customError[`nominee[${nomineeNumber}].appointeePincode`] ||
                    getIn(errors, `nominee[${nomineeNumber}].appointeePincode`)
                  }
                  touched={getIn(touched, `nominee[${nomineeNumber}].appointeePincode`)}
                />
              )}
            </div>
            {/* {isFieldVisible("captureAppointeeAddress", true) && (
              <div className="mt-5 ">
                <CustomCheckBox
                  label={getFieldLabel("captureAppointeeAddress", true)}
                  name={`nominee[${nomineeNumber}].captureAppointeeAddress`}
                  handleBlur={handleBlur}
                  setFieldValue={setFieldValue}
                  checked={
                    values?.nominee[nomineeNumber]?.captureAppointeeAddress ??
                    getFieldDefaultValue("captureAddress", true)
                  }
                />
              </div>
            )} */}
            <div className="display-flex grid grid-cols-1 mt-5 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-10 gap-y-6">
              {isFieldVisible("isAddressSameAsLA", true) && (
                <div className="mt-5">
                  <CustomCheckBox
                    label={getFieldLabel("isAddressSameAsLA", true)}
                    name={`nominee[${nomineeNumber}].isAppointeeAddressSameAsProposer`}
                    handleBlur={handleBlur}
                    setFieldValue={setFieldValue}
                    checked={
                      values?.nominee[nomineeNumber]?.isAppointeeAddressSameAsProposer ??
                      getFieldDefaultValue("isAddressSameAsLA", true)
                    }
                  />
                </div>
              )}
              {isFieldVisible("captureAppointeeBankDetails", true) && (
                <div className="mt-5">
                  <CustomCheckBox
                    label={getFieldLabel("captureAppointeeBankDetails", true)}
                    name={`nominee[${nomineeNumber}].captureAppointeeBankDetails`}
                    handleBlur={handleBlur}
                    setFieldValue={setFieldValue}
                    checked={
                      values?.nominee[nomineeNumber]?.captureAppointeeBankDetails ??
                      getFieldDefaultValue("captureAppointeeBankDetails", true)
                    }
                  />
                </div>
              )}
            </div>
            {isFieldVisible("bankDetails", true) &&
              values?.nominee[nomineeNumber]?.captureAppointeeBankDetails && (
                <div className="grid grid-cols-1 mt-5 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-10 gap-y-6">
                  <CustomTextField
                    requiredStar={
                      isFieldRequired("appointeeBankName", true) || isAnyAppointeeBankFieldFilled
                    }
                    label={getFieldLabel("appointeeBankName", true)}
                    onChange={handleChange}
                    handleBlur={handleBlur}
                    value={
                      values?.nominee[nomineeNumber]?.appointeeBankName ??
                      getFieldDefaultValue("appointeeBankName", true)
                    }
                    name={`nominee[${nomineeNumber}].appointeeBankName`}
                    error={getIn(errors, `nominee[${nomineeNumber}].appointeeBankName`)}
                    touched={getIn(touched, `nominee[${nomineeNumber}].appointeeBankName`)}
                  />
                  <CustomTextField
                    requiredStar={
                      isFieldRequired("appointeeBankAccountNumber", true) ||
                      isAnyAppointeeBankFieldFilled
                    }
                    label={getFieldLabel("appointeeBankAccountNumber", true)}
                    onChange={handleChange}
                    handleBlur={handleBlur}
                    value={
                      values?.nominee[nomineeNumber]?.appointeeBankAccountNumber ??
                      getFieldDefaultValue("appointeeBankAccountNumber", true)
                    }
                    name={`nominee[${nomineeNumber}].appointeeBankAccountNumber`}
                    error={getIn(errors, `nominee[${nomineeNumber}].appointeeBankAccountNumber`)}
                    touched={getIn(touched, `nominee[${nomineeNumber}].appointeeBankAccountNumber`)}
                  />
                  <CustomTextField
                    requiredStar={
                      isFieldRequired("appointeeIfscCode", true) || isAnyAppointeeBankFieldFilled
                    }
                    label={getFieldLabel("appointeeIfscCode", true)}
                    onChange={(e) =>
                      handleIfscCodeValidation(e, `nominee[${nomineeNumber}].appointeeIfscCode`)
                    }
                    handleBlur={handleBlur}
                    value={
                      values?.nominee[nomineeNumber]?.appointeeIfscCode ??
                      getFieldDefaultValue("appointeeIfscCode", true)
                    }
                    name={`nominee[${nomineeNumber}].appointeeIfscCode`}
                    error={
                      customError[`nominee[${nomineeNumber}].appointeeIfscCode`] ||
                      getIn(errors, `nominee[${nomineeNumber}].appointeeIfscCode`)
                    }
                    touched={getIn(touched, `nominee[${nomineeNumber}].appointeeIfscCode`)}
                  />
                </div>
              )}
          </div>
        )}
      </div>
    </div>
  );
}

export default memo(PrimaryDetails);
